import { Grid } from '@mantine/core'
import { translate } from 'i18n/i18n'
import moment from 'moment-timezone'
import React from 'react'
import { getActiveNodeStatus, NODE_STATUS_COLORS } from '../../../Map/_utils/getActiveNodeStatus'
import { ToggleSymbolValue } from '../../../ToggleSymbolValue/ToggleSymbolValue'
import type { TNodeType, TSnNodeLog } from '../../../types'
import { renderWithDefaultValue } from '../../../utils/renderers'
import { getSnNodeLogSignalRSSI } from '../../utils/getSnNodeLogSignalRSSI'
import { BatteryStrength } from './BatteryStrength'
import { SignalStrength } from './SignalStrength'

type SnNodeLogProps = {
  log: TSnNodeLog | undefined
  nodeType: TNodeType
  timezone: string
}

export const SnNodeLog: React.FC<SnNodeLogProps> = ({ log, nodeType, timezone }) => {
  const lastLogTimeAgoText = log ? (
    <ToggleSymbolValue value={moment.tz(log.stamp, timezone).format('lll z')}>
      {moment.tz(log.stamp, 'UTC').fromNow()}
    </ToggleSymbolValue>
  ) : (
    translate.phrases.placeholder('Never')
  )

  const nodeStatus = getActiveNodeStatus(log)
  const nodeStatusColor = NODE_STATUS_COLORS[nodeStatus]

  return (
    <>
      <Grid.Col span={4}>{translate.phrases.placeholder('Last Log')}</Grid.Col>
      <Grid.Col span={8}>
        <span css={{ color: nodeStatusColor, fontWeight: 500 }}>
          {renderWithDefaultValue(lastLogTimeAgoText)}
        </span>
      </Grid.Col>
      {!!log && (
        <>
          <Grid.Col span={4}>{translate.phrases.placeholder('Signal Strength')}</Grid.Col>
          <Grid.Col span={8}>
            <ToggleSymbolValue value={`${log.signal ? `${log.signal * -1} dBm` : 'unknown'}`}>
              <SignalStrength signalRSSI={getSnNodeLogSignalRSSI(log.signal)} />
            </ToggleSymbolValue>
          </Grid.Col>
          {log.battery && (
            <>
              <Grid.Col span={4}>{translate.phrases.placeholder('Battery')}</Grid.Col>
              <Grid.Col span={8}>
                <span css={{ fontWeight: 500 }}>
                  <BatteryStrength nodeType={nodeType} voltage={log.battery} />
                </span>
              </Grid.Col>
            </>
          )}
          {(nodeType === 'rpt' || nodeType === 'y') && (
            <>
              {log.lithium !== undefined && (
                <>
                  <Grid.Col span={4}>{translate.phrases.placeholder('Lithium')}</Grid.Col>
                  <Grid.Col span={8}>
                    <span css={{ fontWeight: 500 }}>
                      <BatteryStrength nodeType={nodeType} voltage={log.lithium} />
                    </span>
                  </Grid.Col>
                </>
              )}
              {log.solar !== undefined && (
                <>
                  <Grid.Col span={4}>{translate.phrases.placeholder('Solar')}</Grid.Col>
                  <Grid.Col span={8}>
                    <span css={{ fontWeight: 500 }}>
                      <BatteryStrength nodeType={nodeType} voltage={log.solar} />
                    </span>
                  </Grid.Col>
                </>
              )}
            </>
          )}
        </>
      )}
    </>
  )
}
